import { useCallback, useMemo, useRef, useState } from 'react';
import {
    IconCrop,
    IconExternalLink,
    IconFileTypePdf,
    IconLayoutColumns,
    IconPrinter,
    IconRefresh,
    IconRotateClockwise,
    IconSettings,
} from '@tabler/icons-react';
import { notifications } from '@/lib/notifications';
import { useTranslation } from '../../../../../i18n/hooks/useTranslation';
import { toBnDigits } from '../../../../../utils/bnFormat';
import { recordToolUsage } from '../../../../../services/toolsService';
import { reportBoothDone } from '../../../../../utils/boothPrivacy';
import ToolPageLayout from '../../components/common/ToolPageLayout';
import ToolBoothTabs from '../../components/common/ToolBoothTabs';
import ToolPreviewPanel from '../../components/common/ToolPreviewPanel';
import ToolCard from '../../components/common/ToolCard';
import useImagePaste from '../../components/image/useImagePaste';
import SettingSlider from '../shared/SettingSlider';
import ApplyToAllButton from '../shared/ApplyToAllButton';
import DocumentCleanupCropModal from '../document-cleanup/DocumentCleanupCropModal';
import DocumentCleanupPreview from '../document-cleanup/DocumentCleanupPreview';
import DocumentCleanupPageRail from '../document-cleanup/DocumentCleanupPageRail';
import useDocumentCleanupPages from '../document-cleanup/useDocumentCleanupPages';
import { printImageDataUrl } from '../document-cleanup/printDocumentImage';
import { buildPagesPdf } from '../document-cleanup/pagesPdf';
import { ACCEPT, DEFAULT_ADJUSTMENTS, imageDataToDataUrl } from '../document-cleanup/documentCleanupEngine';
import { MAX_PAGES } from '../document-cleanup/documentCleanupPages';
import { deriveToolStatus, downloadBlob, getToolStatusText } from '../../toolPageUtils';

/** Photos and PDFs — a PDF drops in as one page per page. */
const PICKER_ACCEPT = `${ACCEPT},application/pdf,.pdf`;
/** This tool works on everything the customers send: the photos AND the PDF files. */
const BOOTH_KINDS = ['image', 'file'];
const SLIDERS = ['brightness', 'contrast'];
// Colour modes: normal = the original colours, bw = true 1-bit black & white (the cleanup engine's 'bw' preset), gray = shades of grey.
const MODES = ['normal', 'bw', 'gray'];
const modeOf = (page) => (page?.preset === 'bw' ? 'bw' : page?.adjustments?.grayscale ? 'gray' : 'normal');

/** Do two pages carry the same adjustments? (every key the engine knows — a missing one counts as untouched) */
const sameAdjustments = (a, b) => Object.keys(DEFAULT_ADJUSTMENTS).every((key) => (a?.[key] || 0) === (b?.[key] || 0));

/**
 * Document Print (print-ready-maker) — get what customers send ready for the printer: brighten or darken it, crop it, turn it, then print.
 *
 * The Photo Booth queue (photos and PDFs) sits in the left column; a tap brings a customer's file in as pages. Everything else —
 * the page rail, the crop, the preview, the print sheet — is the Document Cleanup machinery, so the two tools behave the same.
 *
 * Privacy: a file the CUSTOMER marked private is deleted from the booth as soon as it has been printed or downloaded here
 * (utils/boothPrivacy.js). The pages stay in this tab until it is closed, so a jammed printer can simply print again.
 */
export default function PrintReadyMaker() {
    const { t } = useTranslation();
    const fileInputRef = useRef(null);

    const [cropOpen, setCropOpen] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [finished, setFinished] = useState(false);
    const [error, setError] = useState(null);
    const [boothOn, setBoothOn] = useState(false);
    const [boothRefresh, setBoothRefresh] = useState(0);

    const reportError = useCallback((reason) => {
        const message = t(`printReadyMaker.errors.${reason}`, { defaultValue: t('printReadyMaker.errors.failed') });
        setError(message);
        notifications.show({ title: t('common.error'), message, color: 'red' });
    }, [t]);

    const {
        pages,
        selected,
        selectedId,
        select,
        addFiles,
        removePage,
        reorder,
        clear,
        previewResult,
        previewSource,
        baseUrl,
        sourceSize,
        busy,
        importing,
        compare,
        setCompare,
        setAdjustment,
        setPreset,
        resetAdjustments,
        applyToAll,
        rotate,
        applyCrop,
        renderPageFull,
    } = useDocumentCleanupPages({ onError: reportError, adjustThumbs: true });

    const working = busy || importing || exporting;

    // ── getting files in ──
    const handleAdd = useCallback((incoming) => {
        setError(null);
        setFinished(false);
        void addFiles(incoming);
    }, [addFiles]);

    // Ctrl/Cmd+V anywhere on the page drops copied pictures straight in.
    useImagePaste(handleAdd, { enabled: !working });

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        handleAdd(e.dataTransfer.files);
    };

    // A customer's file from the Photo Booth: its pages are tagged with the booth id, whether it is private, and whether only PART of it
    // fitted (the tool holds MAX_PAGES at once) — a file that came in only in part must not be deleted from the booth when what we have is printed.
    // `boothInFlight` is a ref: a second tap while a big PDF is still being turned into pages would otherwise (the pages not being in state
    // yet) bring the same file in twice.
    const boothInFlight = useRef(new Set());
    const handleBoothPick = useCallback(async (file, uploadId, meta) => {
        const have = pages.find((p) => p.booth?.id === uploadId);
        if (have) { select(have.id); return; } // already here — just show it
        if (boothInFlight.current.has(uploadId)) return;
        boothInFlight.current.add(uploadId);
        try {
            setError(null);
            setFinished(false);
            const expected = meta?.kind === 'file' ? meta?.pageCount : 1;
            const partial = Number.isFinite(expected) && expected > MAX_PAGES - pages.length;
            await addFiles([file], { booth: { id: uploadId, isPrivate: meta?.isPrivate === true, partial } });
        } finally {
            boothInFlight.current.delete(uploadId);
        }
    }, [addFiles, pages, select]);

    const openPicker = () => fileInputRef.current?.click();

    const handleReset = useCallback(() => {
        clear();
        setCropOpen(false);
        setFinished(false);
        setError(null);
    }, [clear]);

    // ── getting it out ──
    /** Every page, adjusted at full resolution, in rail order. */
    const renderAll = useCallback(async () => {
        const out = [];
        for (const page of pages) {
            const result = await renderPageFull(page.id);
            if (result) out.push({ page, result });
        }
        return out;
    }, [pages, renderPageFull]);

    /** What was just printed / downloaded: the customer's PRIVATE files that went into it are deleted from the booth. */
    const finishBooth = useCallback(async (rendered) => {
        const ids = rendered.map(({ page }) => (page.booth?.isPrivate && !page.booth.partial ? page.booth.id : null)).filter(Boolean);
        const deleted = await reportBoothDone(ids);
        if (!deleted.length) return;
        setBoothRefresh((n) => n + 1);
        notifications.show({
            title: t('printReadyMaker.privateDeletedTitle'),
            message: t('printReadyMaker.privateDeleted', { count: deleted.length, n: toBnDigits(deleted.length) }),
            color: 'green',
        });
    }, [t]);

    const handlePrint = useCallback(() => {
        if (!pages.length || working) return;
        setExporting(true);
        void (async () => {
            try {
                const rendered = await renderAll();
                if (!rendered.length) return;
                recordToolUsage('image/print-ready-maker', { category: 'image' });
                // Paper size, orientation and copies are chosen in the print dialog itself.
                await printImageDataUrl(
                    rendered.map(({ result }) => imageDataToDataUrl(result, 'image/jpeg', 0.95)),
                    t('printReadyMaker.printTitle'),
                );
                setFinished(true);
                await finishBooth(rendered);
            } catch {
                reportError('failed');
            } finally {
                setExporting(false);
            }
        })();
    }, [finishBooth, pages.length, renderAll, reportError, t, working]);

    const handleDownloadPdf = useCallback(() => {
        if (!pages.length || working) return;
        setExporting(true);
        void (async () => {
            try {
                const rendered = await renderAll();
                if (!rendered.length) return;
                recordToolUsage('image/print-ready-maker', { category: 'image' });
                downloadBlob(await buildPagesPdf(rendered.map(({ result }) => result)), 'print-ready.pdf');
                setFinished(true);
                await finishBooth(rendered);
            } catch {
                reportError('failed');
            } finally {
                setExporting(false);
            }
        })();
    }, [finishBooth, pages.length, renderAll, reportError, working]);

    const hasPages = pages.length > 0;
    const adjustments = selected?.adjustments ?? DEFAULT_ADJUSTMENTS;
    const pageIndex = pages.findIndex((p) => p.id === selectedId);
    const hasPrivate = pages.some((p) => p.booth?.isPrivate);

    // "Apply to the other pages" is only offered when pressing it would DO something: this page has been adjusted, and at least
    // one other page still differs from it. (Once everything matches — or nothing was touched — there is nothing to apply.)
    const canApplyToAll = !!selected
        && (!sameAdjustments(adjustments, DEFAULT_ADJUSTMENTS) || modeOf(selected) !== 'normal')
        && pages.some((p) => p.id !== selectedId && (!sameAdjustments(p.adjustments, adjustments) || modeOf(p) !== modeOf(selected)));
    // The pages' thumbnails stay the originals, so the click itself shows nothing — say what happened.
    const handleApplyToAll = useCallback(() => {
        const others = pages.length - 1;
        applyToAll();
        setFinished(false);
        notifications.show({ message: t('printReadyMaker.appliedToAll', { count: others, n: toBnDigits(others) }), color: 'green' });
    }, [applyToAll, pages.length, t]);

    /** Hover toolbar over the preview image — acts on the SELECTED page. */
    const previewActions = useMemo(() => [
        {
            id: 'crop',
            icon: IconCrop,
            label: t('printReadyMaker.crop'),
            onClick: () => { setCompare(false); setCropOpen(true); },
            disabled: working || !selected,
        },
        {
            id: 'rotate',
            icon: IconRotateClockwise,
            label: t('printReadyMaker.rotate'),
            onClick: rotate,
            disabled: working || !selected,
        },
        {
            id: 'compare',
            icon: IconLayoutColumns,
            label: t('printReadyMaker.compare'),
            onClick: () => setCompare((v) => !v),
            active: compare,
            disabled: working || !previewResult || !previewSource,
        },
    ], [compare, previewResult, previewSource, rotate, selected, setCompare, t, working]);

    const status = deriveToolStatus({
        hasInput: hasPages,
        canRun: !!previewResult,
        processing: working,
        hasResult: finished,
        hasError: !!error,
    });
    const statusBits = [getToolStatusText(status, t, {
        ready: t('toolPage.statusText.ready'),
        success: t('printReadyMaker.done'),
    })];
    if (hasPages) statusBits.push(t('printReadyMaker.pageCount', { count: toBnDigits(pages.length) }));
    if (sourceSize) statusBits.push(`${toBnDigits(sourceSize.width)}×${toBnDigits(sourceSize.height)}`);

    return (
        <>
            <ToolPageLayout
                slug="image/print-ready-maker"
                subtitleKey="printReadyMaker.subtitle"
                status={status}
                statusText={statusBits.join(' · ')}
                onReset={hasPages || error ? handleReset : undefined}
                primaryAction={{
                    label: pages.length > 1
                        ? t('printReadyMaker.printAll', { count: toBnDigits(pages.length) })
                        : t('printReadyMaker.print'),
                    onClick: handlePrint,
                    disabled: !hasPages || working,
                    loading: exporting,
                    icon: IconPrinter,
                }}
                secondaryAction={[
                    {
                        key: 'pdf',
                        label: t('printReadyMaker.downloadPdf'),
                        onClick: handleDownloadPdf,
                        disabled: !hasPages || working,
                        icon: IconFileTypePdf,
                    },
                ]}
                showTrust={false}
                settings={(
                    <ToolBoothTabs
                        kinds={BOOTH_KINDS}
                        initialTab="booth"
                        onPickFile={handleBoothPick}
                        refreshKey={boothRefresh}
                        onStatusChange={setBoothOn}
                        footer={(
                            // A new tab on purpose: leaving this page would throw away the pages already loaded and adjusted here.
                            <a
                                href="/settings?tab=photoeditsettings"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-3 rounded-xl border border-zinc-200 bg-white px-3.5 py-3 transition-colors hover:border-amber-400 hover:bg-amber-50/40 dark:border-zinc-700 dark:bg-zinc-900 dark:hover:border-amber-500/60 dark:hover:bg-amber-500/5"
                            >
                                <span className="flex h-8 w-8 flex-none items-center justify-center rounded-lg bg-amber-50 text-amber-600 dark:bg-amber-500/10 dark:text-amber-400">
                                    <IconSettings size={17} stroke={1.8} />
                                </span>
                                <span className="min-w-0 flex-1">
                                    <span className="block text-[13px] font-semibold text-zinc-800 dark:text-zinc-100">{t('printReadyMaker.boothSettings')}</span>
                                    <span className="block text-[11.5px] leading-snug text-zinc-500 dark:text-zinc-400">{t('printReadyMaker.boothSettingsHint')}</span>
                                </span>
                                <IconExternalLink size={15} stroke={1.8} className="flex-none text-zinc-400 dark:text-zinc-500" />
                            </a>
                        )}
                    >
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept={PICKER_ACCEPT}
                            multiple
                            className="hidden"
                            onChange={(e) => { handleAdd(Array.from(e.target.files || [])); e.target.value = ''; }}
                        />

                        <ToolCard
                            title={selected && pageIndex >= 0
                                ? t('printReadyMaker.adjustTitleFor', { page: toBnDigits(pageIndex + 1) })
                                : t('printReadyMaker.adjustTitle')}
                            headerAction={selected ? (
                                <button
                                    type="button"
                                    onClick={() => { resetAdjustments(); setPreset('none'); setFinished(false); }}
                                    disabled={working}
                                    className="inline-flex items-center gap-1 rounded-lg px-1.5 py-1 text-[11.5px] font-semibold text-[#6B635C] transition-colors hover:text-amber-600 disabled:cursor-not-allowed disabled:opacity-45 dark:text-zinc-400 dark:hover:text-amber-400"
                                >
                                    <IconRefresh size={14} stroke={2} />
                                    {t('printReadyMaker.reset')}
                                </button>
                            ) : null}
                        >
                            {selected ? (
                                <div className="space-y-3.5">
                                    {SLIDERS.map((key) => (
                                        <SettingSlider
                                            key={key}
                                            id={`print-ready-${key}`}
                                            label={t(`printReadyMaker.sliders.${key}`)}
                                            value={adjustments[key] || 0}
                                            min={-100}
                                            max={100}
                                            disabled={working}
                                            onChange={(value) => { setAdjustment(key, value); setFinished(false); }}
                                        />
                                    ))}
                                    <div>
                                        <div className="mb-1.5 text-[13.5px] font-semibold text-[#1C1815] dark:text-zinc-100">{t('printReadyMaker.modeTitle')}</div>
                                        <div className="grid grid-cols-3 gap-1.5">
                                            {MODES.map((mode) => (
                                                <button
                                                    key={mode}
                                                    type="button"
                                                    disabled={working}
                                                    onClick={() => {
                                                        setPreset(mode === 'bw' ? 'bw' : 'none');
                                                        setAdjustment('grayscale', mode === 'gray' ? 1 : 0);
                                                        setFinished(false);
                                                    }}
                                                    className={`rounded-lg border px-2 py-2 text-xs font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-45 ${
                                                        modeOf(selected) === mode
                                                            ? 'border-primary bg-primary/10 text-primary'
                                                            : 'border-zinc-200 text-zinc-600 hover:bg-zinc-50 dark:border-zinc-700 dark:text-zinc-300 dark:hover:bg-zinc-800'
                                                    }`}
                                                >
                                                    {t(`printReadyMaker.modes.${mode}`)}
                                                </button>
                                            ))}
                                        </div>
                                        <p className="mt-1.5 text-[11.5px] leading-snug text-[#A8A099] dark:text-zinc-500">{t(`printReadyMaker.modeHints.${modeOf(selected)}`)}</p>
                                    </div>
                                    {canApplyToAll ? (
                                        <ApplyToAllButton onClick={handleApplyToAll} disabled={working}>
                                            {t('printReadyMaker.applyToAll', { count: toBnDigits(pages.length - 1) })}
                                        </ApplyToAllButton>
                                    ) : null}
                                </div>
                            ) : (
                                <p className="text-[11.5px] leading-relaxed text-[#A8A099] dark:text-zinc-500">{t('printReadyMaker.adjustHint')}</p>
                            )}
                        </ToolCard>

                        {hasPrivate ? (
                            <p className="rounded-xl bg-amber-50 px-3 py-2 text-[11.5px] leading-snug text-amber-800 dark:bg-amber-500/10 dark:text-amber-300">
                                {t('printReadyMaker.privateNotice')}
                            </p>
                        ) : null}
                    </ToolBoothTabs>
                )}
                preview={(
                    <ToolPreviewPanel
                        title={t('printReadyMaker.previewTitle')}
                        status={status}
                        statusLabel={t(`toolPage.status.${status}`)}
                        idleTitle={t('printReadyMaker.previewEmpty')}
                        idleHint={t(boothOn ? 'printReadyMaker.previewEmptyHint' : 'printReadyMaker.previewEmptyHintNoBooth')}
                        processingLabel={importing ? t('printReadyMaker.importing') : t('printReadyMaker.processing')}
                        error={error}
                        canvasTone="plain"
                        processingTone="veil"
                        showPasteHint
                        showIdle={!hasPages && !error}
                        onIdleClick={openPicker}
                        onIdleDrop={handleDrop}
                        onIdleDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                        onIdleDragLeave={() => setIsDragging(false)}
                        idleDragging={isDragging}
                    >
                        {hasPages ? (
                            <div
                                className="flex h-full min-h-0 w-full gap-3"
                                onDrop={handleDrop}
                                onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                                onDragLeave={() => setIsDragging(false)}
                            >
                                <div className="w-[86px] flex-none sm:w-[104px]">
                                    <DocumentCleanupPageRail
                                        pages={pages}
                                        selectedId={selectedId}
                                        onSelect={select}
                                        onRemove={removePage}
                                        onReorder={reorder}
                                        onAdd={openPicker}
                                        disabled={working || pages.length >= MAX_PAGES}
                                        labels={{
                                            add: t('printReadyMaker.addPages'),
                                            remove: t('printReadyMaker.removePage'),
                                            page: t('printReadyMaker.page'),
                                            loading: t('printReadyMaker.processing'),
                                            private: t('printReadyMaker.privateLock'),
                                        }}
                                    />
                                </div>
                                <div className="min-h-0 min-w-0 flex-1">
                                    <DocumentCleanupPreview
                                        imageData={previewResult}
                                        compareImageData={previewSource}
                                        compare={compare}
                                        labels={{
                                            cleaned: t('printReadyMaker.editedBadge'),
                                            original: t('printReadyMaker.originalBadge'),
                                            compare: t('printReadyMaker.compare'),
                                        }}
                                        actions={previewActions}
                                    />
                                </div>
                            </div>
                        ) : null}
                    </ToolPreviewPanel>
                )}
            />

            <DocumentCleanupCropModal
                opened={cropOpen}
                onClose={() => setCropOpen(false)}
                imageSrc={baseUrl}
                initialQuad={selected?.quad ?? null}
                fileName={selected?.name}
                onApply={(quad) => { setCropOpen(false); setFinished(false); applyCrop(quad); }}
                busy={working}
            />
        </>
    );
}
